import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CONTACT_HERO_ARIA, CONTACT_HERO_PRODUCTS } from "./contactHeroProducts";

/**
 * Contact-hero product showcase: Shingare's toolkit as one layered, art-directed composition rather than a row or a grid —
 * a dominant tube cleaner anchoring the centre, pipe beveling machines behind it on the left, a tube-expansion tool and a
 * hydraulic torque wrench overlapping on the right. Real product cutouts only (see contactHeroProducts.js).
 *
 * Atmosphere (all static, navy-on-navy): soft radial light behind the anchor, a faint technical grid, two hairline rings
 * with tick marks, a scatter of tiny particles, and a floor glow. Each product gets a contact shadow, a hairline blue rim
 * light on its upper-left edge (an offset drop-shadow of its own silhouette — no repainting of the photo).
 * Motion: a single staggered entrance (0 / 100 / 180 / 260 ms, 0.8 s ease-out, 15–20 px rise) and a pointer parallax capped
 * at ~9 px. No idle animation. Both are skipped for prefers-reduced-motion.
 * Phones show 2 products, tablets 3, desktop all 4; captions from 1280 px up.
 */
const RIM = "[filter:drop-shadow(-1px_-1px_0_rgba(140,185,255,0.38))_drop-shadow(0_20px_18px_rgba(2,10,30,0.55))]";

// deterministic scatter (no Math.random: the picture must not change between renders)
const PARTICLES = Array.from({ length: 22 }, (_, i) => {
  const a = (i * 137.508) % 360, r = 18 + ((i * 53) % 34);
  return { x: 50 + r * Math.cos((a * Math.PI) / 180) * 1.25, y: 52 + r * Math.sin((a * Math.PI) / 180) * 0.95, o: 0.14 + ((i * 7) % 5) * 0.05, s: i % 4 === 0 ? 0.55 : 0.32 };
}).filter((p) => p.x > 2 && p.x < 98 && p.y > 4 && p.y < 96);

const vars = (layout) => {
  const v = {};
  [["base", 0], ["md", 1], ["lg", 2]].forEach(([k, i]) => {
    if (layout[k]) { v[`--l${i}`] = `${layout[k].l}%`; v[`--t${i}`] = `${layout[k].t}%`; v[`--w${i}`] = `${layout[k].w}%`; }
  });
  return v;
};
const visibility = (layout) => (layout.base ? "" : layout.md ? "hidden md:block" : "hidden lg:block");

export default function ContactHeroVisual() {
  const reduce = useReducedMotion();
  const stageRef = useRef(null);

  // pointer parallax: normalised position over the whole hero, applied as two CSS variables (no React re-render)
  useEffect(() => {
    const el = stageRef.current;
    const host = el?.closest("section");
    if (!el || !host || reduce) return undefined;
    let raf = 0;
    const move = (e) => {
      if (e.pointerType !== "mouse") return;
      const r = host.getBoundingClientRect();
      const x = ((e.clientX - r.left) / r.width - 0.5) * 2, y = ((e.clientY - r.top) / r.height - 0.5) * 2;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => { el.style.setProperty("--px", x.toFixed(3)); el.style.setProperty("--py", y.toFixed(3)); });
    };
    host.addEventListener("pointermove", move);
    return () => { cancelAnimationFrame(raf); host.removeEventListener("pointermove", move); };
  }, [reduce]);

  return (
    <div
      ref={stageRef}
      role="img"
      aria-label={CONTACT_HERO_ARIA}
      data-testid="contact-hero-visual"
      className="pointer-events-none relative mx-auto aspect-[1.32/1] w-full max-w-[440px] md:aspect-[0.98/1] md:max-w-none lg:aspect-[1.22/1]"
    >
      {/* atmosphere */}
      <div className="absolute -inset-10 bg-[radial-gradient(ellipse_46%_44%_at_52%_60%,rgba(23,105,255,0.34),transparent_72%)]" aria-hidden="true" />
      <div className="absolute inset-0 opacity-[0.07] [background-image:linear-gradient(to_right,#9CC3FF_1px,transparent_1px),linear-gradient(to_bottom,#9CC3FF_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_62%_58%_at_52%_54%,#000,transparent)]" aria-hidden="true" />
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <g fill="none" stroke="rgba(127,176,255,0.15)" strokeWidth="0.25" vectorEffect="non-scaling-stroke">
          <circle cx="52" cy="56" r="34" />
          <circle cx="52" cy="56" r="49" strokeDasharray="0.6 1.5" />
          {/* tick marks where the axes meet the inner ring */}
          <path d="M52 20.5v3M52 88.5v3M16.5 56h3M84.5 56h3" strokeWidth="0.35" stroke="rgba(156,195,255,0.28)" />
        </g>
        <g fill="#9CC3FF">{PARTICLES.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={p.s} opacity={p.o} />)}</g>
      </svg>
      <div className="absolute inset-x-[6%] bottom-[2%] h-[16%] rounded-[50%] bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.26),transparent_70%)]" aria-hidden="true" />

      <ul className="absolute inset-0 m-0 list-none p-0" aria-hidden="true">
        {CONTACT_HERO_PRODUCTS.map((p) => (
          <li
            key={p.id}
            className={`absolute left-[var(--l0)] top-[var(--t0)] w-[var(--w0)] md:left-[var(--l1)] md:top-[var(--t1)] md:w-[var(--w1)] lg:left-[var(--l2)] lg:top-[var(--t2)] lg:w-[var(--w2)] ${visibility(p.layout)}`}
            style={{ ...vars(p.layout), zIndex: p.z }}
          >
            {/* parallax layer */}
            <div style={reduce ? undefined : { transform: `translate3d(calc(var(--px, 0) * ${p.depth} * -9px), calc(var(--py, 0) * ${p.depth} * -6px), 0)`, transition: "transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)" }}>
              {/* entrance layer */}
              <motion.div
                initial={reduce ? false : { opacity: 0, y: p.dy }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 + p.delay, ease: "easeOut" }}
                className="relative"
              >
                <span className="absolute inset-x-[8%] bottom-[-3%] -z-10 h-[10%] rounded-[50%] bg-[#020a1e]/75 blur-[12px]" />
                <img src={p.src} alt="" width={p.w} height={p.h} decoding="async" loading="eager" draggable="false" className={`block h-auto w-full ${RIM}`} />
              </motion.div>
            </div>
          </li>
        ))}
      </ul>

      {/* tiny technical captions — wide desktop only, purely decorative */}
      <div className="absolute inset-0 hidden xl:block" aria-hidden="true">
        {CONTACT_HERO_PRODUCTS.map((p) => (
          <motion.span
            key={p.id}
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 + p.delay, ease: "easeOut" }}
            className={`absolute flex items-center gap-2 whitespace-nowrap text-[9.5px] font-semibold uppercase tracking-[0.22em] text-[#8DB8FF]/80 ${p.label.align === "right" ? "-translate-x-full flex-row-reverse" : ""}`}
            style={{ left: `${p.label.lx}%`, top: `${p.label.ly}%`, zIndex: 40 }}
          >
            <span className="h-[5px] w-[5px] rounded-full bg-[#1769FF] shadow-[0_0_0_3px_rgba(23,105,255,0.22)]" />
            {p.label.text}
          </motion.span>
        ))}
      </div>
    </div>
  );
}
